import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["available","occupied","leased","daily_rental","maintenance","owner_reserved","reserved","guest","staff","ev"];
const TYPES = ["owner","owner_leased","owner_daily_rental","guest","reserved","maintenance","disabled","staff","ev"];

export default function Slots() {
  const [slots, setSlots] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/slots").then(r => setSlots(r.data));
  useEffect(() => { load(); }, []);

  const filtered = slots.filter(s => s.slot_number.toLowerCase().includes(filter.toLowerCase()));

  const save = async () => {
    try {
      const payload = {
        slot_type: editing.slot_type,
        status: editing.status,
        rate_hourly: Number(editing.rate_hourly) || 0,
        rate_daily: Number(editing.rate_daily) || 0,
        rate_monthly: Number(editing.rate_monthly) || 0,
        revenue_share: editing.revenue_share,
        is_active: editing.is_active,
        notes: editing.notes,
      };
      await api.patch(`/slots/${editing.id}`, payload);
      toast.success("Slot updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500">Slots</p>
          <h1 className="font-heading text-4xl font-black tracking-tighter mt-1">Slot management.</h1>
          <p className="text-sm text-zinc-500 mt-1">Admins cannot change ownership — use the ownership-transfer workflow.</p>
        </div>
        <Input
          placeholder="Search slot number…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs bg-[#141414] border-zinc-800"
          data-testid="slot-search"
        />
      </header>

      <Card className="bg-[#141414] border-zinc-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F10] sticky top-0">
              <tr className="text-left text-zinc-500 text-xs font-mono uppercase">
                <th className="px-4 py-3">Slot</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Hourly</th>
                <th className="px-4 py-3">Monthly</th>
                <th className="px-4 py-3">Rev Share (O/C/M)</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {filtered.map(s => (
                <tr key={s.id} className="hover:bg-zinc-900/50">
                  <td className="px-4 py-3 font-mono">{s.slot_number}</td>
                  <td className="px-4 py-3 capitalize text-zinc-300">{s.slot_type.replace(/_/g," ")}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-2 text-xs`}>
                      <span className={`h-2.5 w-2.5 rounded-sm slot-${s.status}`} />
                      {s.status.replace(/_/g," ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-zinc-400">{s.owner_user_id ? s.owner_user_id.slice(0,8) : "—"}</td>
                  <td className="px-4 py-3 font-mono">₱{s.rate_hourly}</td>
                  <td className="px-4 py-3 font-mono">₱{s.rate_monthly}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.revenue_share.owner}/{s.revenue_share.condo}/{s.revenue_share.manager}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing({ ...s })}
                      data-testid={`edit-slot-${s.slot_number}`}
                      className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800"
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0F0F10] border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Slot {editing?.slot_number}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Type</Label>
                  <Select value={editing.slot_type} onValueChange={(v) => setEditing({ ...editing, slot_type: v })}>
                    <SelectTrigger className="bg-[#141414] border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger className="bg-[#141414] border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Hourly</Label>
                  <Input type="number" value={editing.rate_hourly} onChange={(e) => setEditing({ ...editing, rate_hourly: e.target.value })} className="bg-[#141414] border-zinc-800" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Monthly</Label>
                  <Input type="number" value={editing.rate_monthly} onChange={(e) => setEditing({ ...editing, rate_monthly: e.target.value })} className="bg-[#141414] border-zinc-800" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Revenue Share %</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {["owner","condo","manager"].map(k => (
                    <Input
                      key={k}
                      type="number"
                      value={editing.revenue_share[k]}
                      onChange={(e) => setEditing({ ...editing, revenue_share: { ...editing.revenue_share, [k]: Number(e.target.value) } })}
                      placeholder={k}
                      className="bg-[#141414] border-zinc-800 font-mono"
                    />
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-1 font-mono">Owner / Condo / Manager (must sum to 100)</p>
              </div>
              <Button onClick={save} className="w-full bg-white text-black hover:bg-zinc-200" data-testid="slot-save-button">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
